import React, { useState, useMemo } from "react";
import { Search, ExternalLink, Filter, X, Sparkles } from "lucide-react";
import { resinsData, resinCategories } from "../data/mock";
import { ResinGeniusModal } from "./ResinGeniusModal";

export const MaterialsSection = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [resinGeniusOpen, setResinGeniusOpen] = useState(false);

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set();
    resinsData.forEach((r) => r.tags.forEach((t) => tags.add(t)));
    return Array.from(tags).sort();
  }, []);

  // Filter resins
  const filtered = useMemo(() => {
    return resinsData.filter((resin) => {
      const matchCategory =
        activeCategory === "All" || resin.category === activeCategory;
      const matchSearch =
        !searchQuery ||
        resin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resin.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resin.tags.some((t) =>
          t.toLowerCase().includes(searchQuery.toLowerCase())
        );
      const matchTag = !activeTag || resin.tags.includes(activeTag);
      return matchCategory && matchSearch && matchTag;
    });
  }, [activeCategory, searchQuery, activeTag]);

  return (
    <section
      id="materials"
      className="py-24 lg:py-32 bg-[#F8F8F8] relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-[#F76B1C]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#F76B1C]/[0.02] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em]">
            Materials
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111111] leading-tight">
            45+ Advanced Resins for Every Application
          </h2>
          <p className="mt-6 text-lg text-[#666666] leading-relaxed">
            From general-purpose prototyping to biocompatible medical devices
            and investment casting — find the right resin for your workflow.
          </p>
          <button
            onClick={() => setResinGeniusOpen(true)}
            data-testid="resin-genius-btn"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#F76B1C] hover:bg-[#E05A0F] text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl hover:shadow-[#F76B1C]/20 text-sm group"
          >
            <Sparkles size={18} className="group-hover:animate-pulse" />
            Ask Resin Genius AI
          </button>
        </div>

        {/* Search + Filters bar */}
        <div className="mb-8 space-y-4">
          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999]"
            />
            <input
              type="text"
              placeholder="Search resins by name or property..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-[#E0E0E0] rounded-xl text-sm text-[#111111] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#F76B1C] focus:ring-1 focus:ring-[#F76B1C]/20 transition-all shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#AAAAAA] hover:text-[#666666]"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Category tabs */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {resinCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setActiveTag(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-[#F76B1C] text-white shadow-lg shadow-[#F76B1C]/20"
                    : "bg-white text-[#666666] hover:text-[#111111] hover:bg-[#F0F0F0] border border-[#E0E0E0] shadow-sm"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Property tags */}
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            <Filter size={14} className="text-[#CCCCCC] mr-1" />
            {allTags.slice(0, 12).map((tag) => (
              <button
                key={tag}
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                  activeTag === tag
                    ? "bg-[#F76B1C]/10 text-[#F76B1C] border border-[#F76B1C]/30"
                    : "bg-white text-[#888888] hover:text-[#555555] border border-[#E5E5E5] hover:border-[#CCCCCC] shadow-sm"
                }`}
              >
                {tag}
              </button>
            ))}
            {activeTag && (
              <button
                onClick={() => setActiveTag(null)}
                className="px-2 py-1 text-xs text-[#AAAAAA] hover:text-[#666666]"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        <div className="mb-6 text-center">
          <span className="text-sm text-[#999999]">
            Showing {filtered.length} of {resinsData.length} resins
            {activeCategory !== "All" && (
              <span className="text-[#F76B1C] font-medium"> in {activeCategory}</span>
            )}
            {activeTag && (
              <span className="text-[#F76B1C] font-medium"> · {activeTag}</span>
            )}
          </span>
        </div>

        {/* Resin cards grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
          {filtered.map((resin) => (
            <a
              key={resin.id}
              href={resin.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative rounded-2xl bg-white border border-[#EAEAEA] hover:border-[#F76B1C]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#F76B1C]/[0.06] overflow-hidden"
            >
              {/* Image */}
              <div className="relative h-36 lg:h-44 bg-[#F5F5F5] flex items-center justify-center overflow-hidden p-4">
                <img loading="lazy"
                  src={resin.image}
                  alt={resin.name}
                  className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                />
                {/* Category badge */}
                <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full bg-white/80 backdrop-blur-sm text-[10px] font-semibold text-[#666666] border border-[#E5E5E5] shadow-sm">
                  {resin.category}
                </span>
              </div>

              {/* Content */}
              <div className="p-4 lg:p-5">
                <h3 className="text-sm lg:text-base font-bold text-[#111111] mb-1.5 group-hover:text-[#F76B1C] transition-colors duration-200">
                  {resin.name}
                </h3>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {resin.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#FFF5EE] text-[#D4570A] border border-[#F76B1C]/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-xs text-[#888888] leading-relaxed mb-3 line-clamp-2">
                  {resin.description}
                </p>

                {/* Price + CTA */}
                <div className="flex items-center justify-between pt-3 border-t border-[#F0F0F0]">
                  <div>
                    <span className="text-base lg:text-lg font-bold text-[#111111]">
                      {resin.price}
                    </span>
                    <span className="text-[10px] text-[#AAAAAA] ml-1">AUD</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-[#F76B1C]/80 group-hover:text-[#F76B1C] font-medium transition-colors">
                    Details
                    <ExternalLink size={11} />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="text-[#999999] text-lg mb-2">
              No resins match your filters
            </p>
            <button
              onClick={() => {
                setActiveCategory("All");
                setActiveTag(null);
                setSearchQuery("");
              }}
              className="text-[#F76B1C] text-sm font-semibold hover:underline"
            >
              Reset all filters
            </button>
          </div>
        )}

        {/* Bottom note */}
        <div className="mt-12 text-center">
          <p className="text-[#AAAAAA] text-xs mb-4">
            Prices shown in AUD. Contact us for volume pricing
            and availability. Open Material Mode also supports compatible
            third-party resins.
          </p>
          <a
            href="https://formlabs.com/materials/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#F76B1C] hover:text-[#E05A0F] font-semibold text-sm transition-colors duration-200"
          >
            View Full Materials Library on Formlabs
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
      <ResinGeniusModal isOpen={resinGeniusOpen} onClose={() => setResinGeniusOpen(false)} />
    </section>
  );
};
